import {BS2} from "../../BS2";
import {gsap} from "gsap";
import {OpenSans} from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {WildSpinAnimName} from "../../server/BS2Data";
import {BS2Statics} from "../../config/BS2Statics";

/**
 * Created by Jonas Wålekvist on 2023-02-14.
 */

export class LevelBar extends PIXI.Container {
    private _animatedLevelValue: number = 0;
    private _barMask: PIXI.Graphics;
    private _fillEnding: PIXI.Sprite;
    private _barFill: PIXI.Sprite;
    private _textField: PIXI.Text;
    private _animatedLevel: number = 0;
    private _levelThresholds: number[];
    private _barGfxOffset: number = 27;
    private _maxWidth: number = 659;

    constructor(_levelThresholds: number[]) {
        super();
        this.name = "levelBar";
        this._levelThresholds = _levelThresholds;
        this.init();
        this.position.set(4, 650);
    }

    public fadeOutBarGraphic(duration: number = 0.25): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this._barFill, {duration: duration, alpha: 0, ease: "none"}));
        tl.add(gsap.to(this._fillEnding, {duration: duration, alpha: 0, ease: "none"}), "<");
        tl.add(gsap.to(this._textField, {duration: duration, alpha: 0.2, ease: "none"}), "<");
        return tl;
    }

    public fadeInBarGraphic(duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this._barFill, {duration: duration, alpha: 1, ease: "none"}));
        tl.add(gsap.to(this._fillEnding, {duration: duration, alpha: 1, ease: "none"}), "<");
        tl.add(gsap.to(this._textField, {duration: duration, alpha: 1, ease: "none"}), "<");
        return tl;
    }

    public animateTo(targetValue: number, duration: number = 0.5, currentLevel: number,isRestore:boolean=false): gsap.core.Timeline {
        const targetWidth: number = this._maxWidth * targetValue,
            tl: gsap.core.Timeline = gsap.timeline();
        if (targetValue >= 0.9 && targetValue <= 1) {
            tl.add(() => {
                BS2.reelView.bottomStickyWildView.playWildEyesFireAnim(currentLevel, WildSpinAnimName.IDLE_EYES_FIRE_3,isRestore);
            }, 0);
        } else if (targetValue >= 0.8 && targetValue < 0.9) {
            tl.add(() => {
                BS2.reelView.bottomStickyWildView.playWildEyesFireAnim(currentLevel, WildSpinAnimName.IDLE_EYES_FIRE_2,isRestore);
            }, 0);
        } else if (targetValue >= 0.7 && targetValue < 0.8) {
            tl.add(() => {
                BS2.reelView.bottomStickyWildView.playWildEyesFireAnim(currentLevel, WildSpinAnimName.IDLE_EYES_FIRE_1,isRestore);
            }, 0);
        }
        tl.add(gsap.to(this._barMask, {duration: duration, width: targetWidth + this._barGfxOffset}), 0);
        tl.add(gsap.to(this._fillEnding, {duration: duration, x: targetWidth + this._barGfxOffset}), 0);
        tl.add(this.fadeInBarGraphic(0.1), 0);
        return tl;
    }

    public reset(animated: boolean = false, isCalledFromBet: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (animated) {
            tl.add(this.fadeOutBarGraphic());
            if (isCalledFromBet) {
                for (let reelIdx: number = 0; reelIdx < BS2Statics.NR_OF_REELS; reelIdx++) {
                    tl.add(BS2.reelView.bottomStickyWildView.playWildAnimByName(reelIdx, WildSpinAnimName.IDLE_CANDLES, false, true), "<");
                }
            }
        }
        tl.add(() => {
            this._barMask.width = this._barGfxOffset;
            this._fillEnding.x = this._barGfxOffset;
            this._animatedLevelValue = 0;

        });
        return tl;
    }

    public getCountUp(levelValue: number, level: number, duration: number, performXBetAnim: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            onUpdate: () => this.onCountUpNumber()
        });
        tl.add(() => {
            this._animatedLevel = level;
        });
        tl.add(gsap.to(this, {duration: duration, _animatedLevelValue: levelValue, ease: "none"}));
        if (performXBetAnim) {
            tl.add(this.getXBetOnOffAnim(duration), "<");
        }

        return tl;
    }

    private getXBetOnOffAnim(duration: number, currentAlpha: number = this._textField.alpha): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.to(this._textField, {duration: duration / 4, alpha: 0.1, ease: "none"}, "<");
        tl.to(this._textField, {duration: duration / 4, alpha: currentAlpha, ease: "none"});
        tl.to(this._textField, {duration: duration / 4, alpha: 0.1, ease: "none"});
        tl.to(this._textField, {duration: duration / 4, alpha: currentAlpha, ease: "none"});

        return tl;
    }

    private init() {
        this._barMask = new PIXI.Graphics(); //new PIXI.Sprite(PIXI.Texture.WHITE);
        this._barMask.beginFill(0xffffff, 1);
        this._barMask.drawRect(0, 0, 64, 30);
        this._barMask.endFill();
        this._barMask.position.set(0, 0);
        this._barMask.pivot.set(0, this._barMask.height / 2);
        this._barMask.name = "barMask";

        this._fillEnding = new PIXI.Sprite(BS2.assetLoader.getTextures("ritualBar/fillEnd")[0]);
        this._fillEnding.name = "fillEnding";
        this._fillEnding.position.set(this._barGfxOffset, 0);
        this._fillEnding.anchor.set(0.5, 0.5);

        this._barFill = new PIXI.Sprite(BS2.assetLoader.getTextures("ritualBar/fill")[0]);
        this._barFill.name = "barFill";
        this._barFill.position.set(0, 0);
        this._barFill.anchor.set(0, 0.5);
        this._barFill.mask = this._barMask;


        this._textField = new PIXI.Text("", {
            fontFamily: OpenSans.FAMILY,
            fontSize: 30,
            fontWeight: FontWeight.BOLD,
            dropShadow: true,
            dropShadowAlpha: 0.7,
            dropShadowAngle: 0,
            dropShadowBlur: 8,
            dropShadowColor: "#ffffff",
            dropShadowDistance: 0,
            fill: "#ffffff",
            miterLimit: 1,
            padding: ""
        });
        this._textField.blendMode = PIXI.BLEND_MODES.ADD;
        this._textField.position.set(this._barGfxOffset + this._maxWidth / 2, 0);
        this._textField.anchor.set(0.5, 0.5);

        const frame: PIXI.Sprite = new PIXI.Sprite(BS2.assetLoader.getTextures("ritualBar/frame")[0]);
        frame.position.set(3, 0);
        frame.anchor.set(0, 0.5);
        frame.name = "barFrame";


        this.addChild(this._barFill);
        this.addChild(this._fillEnding);
        this.addChild(this._barMask);
        this.addChild(this._textField);
        this.addChild(frame);
    }

    private updateText(levelValue: number, level: number) {
        this._textField.text = `${levelValue} / ${this._levelThresholds[level]}`;
    }

    private onCountUpNumber() {
        this.updateText(Math.ceil(this._animatedLevelValue), this._animatedLevel);
    }
}